/**
 * Cart Badge Component
 *
 * Shows cart icon with item count badge.
 * Used in the header for quick cart access.
 * Includes a live region to announce cart updates to screen readers.
 */

import { useEffect, useRef } from 'react';
import { ShoppingCart } from 'lucide-react';
import { useCartStore } from '@/stores/useCartStore';
import { cn } from '@/lib/utils';

interface CartBadgeProps {
    onClick?: () => void;
    className?: string;
}

export const CartBadge = ({ onClick, className }: CartBadgeProps) => {
    const items = useCartStore((state) => state.items);
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    const previousCount = useRef(itemCount);

    // Determine if we should announce (only on changes, not initial render)
    const shouldAnnounce = useRef(false);

    useEffect(() => {
        if (previousCount.current !== itemCount) {
            shouldAnnounce.current = true;
            previousCount.current = itemCount;
        }
    }, [itemCount]);

    return (
        <>
            <button
                onClick={onClick}
                className={cn(
                    'relative p-2 text-muted-foreground hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md',
                    className
                )}
                aria-label={`Shopping cart${itemCount > 0 ? ` with ${itemCount} item${itemCount === 1 ? '' : 's'}` : ', empty'}`}
            >
                <ShoppingCart className="h-6 w-6" aria-hidden="true" />
                {itemCount > 0 && (
                    <span
                        key={itemCount}
                        className="delight-pop absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-medium text-white"
                        aria-hidden="true"
                    >
                        {itemCount > 99 ? '99+' : itemCount}
                    </span>
                )}
            </button>

            {/* Live region for screen reader announcements */}
            <span
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="sr-only"
            >
                {shouldAnnounce.current && itemCount > 0
                    ? `Cart updated: ${itemCount} item${itemCount === 1 ? '' : 's'} in cart`
                    : ''
                }
            </span>
        </>
    );
};

export default CartBadge;
